import * as THREE from "three";

/**
 * @name getMapCameraTarget
 * @description Finds where the camera's view ray hits the ground plane (y=0) using ray casting.
 * @returns The ground intersection point and the distance from camera to that point.
 *
 * Uses the parametric ray equation: P(t) = origin + t * direction
 * Solving for y=0: t = -origin.y / direction.y
 * @see https://en.wikipedia.org/wiki/Ray_casting#Concept_model
 *
 * ## Edge case: camera looking horizontally (forward.y ≈ 0)
 *
 * When the camera looks parallel to the ground, forward.y approaches 0 and the ray
 * never intersects the ground plane. Without protection, t → ±Infinity which crashes MapLibre.
 *
 * Two approaches are kept here for reference (TODO: remove the unused one after testing):
 *
 * ### Active: Clamp approach
 * Clamps forward.y to at least ~1° downward. This gives a smooth transition —
 * the target point moves far away and zoom becomes very small, which feels natural.
 *
 * ### Alternative: If/else fallback approach (commented out below)
 * When forward.y is too small, falls back to the point directly below the camera.
 * This causes a visible discontinuity — the map center suddenly jumps from a distant
 * point to directly below the camera when crossing the ~89° pitch threshold.
 */
function getMapCameraTarget({ cameraForward, cameraPosition }: { cameraForward: THREE.Vector3; cameraPosition: THREE.Vector3 }) {
	// Clamp forward.y to min ~1° downward (-sin(1°) ≈ -0.017).
	// Without this clamp: when looking horizontally, t = -pos.y / ~0 → Infinity → MapLibre crashes.
	// With this clamp: t becomes large but finite, target point is far away, zoom is very small — smooth behavior.
	const MIN_FORWARD_Y = -Math.sin(THREE.MathUtils.DEG2RAD);
	const forwardY = Math.min(cameraForward.y, MIN_FORWARD_Y);

	const t = -cameraPosition.y / forwardY;
	const x = cameraPosition.x + cameraForward.x * t;
	const z = cameraPosition.z + cameraForward.z * t;

	return { point: new THREE.Vector3(x, 0, z), distance: Math.abs(t) };
}

// --- Alternative: if/else fallback approach ---
// When forward.y is near 0 (camera looking horizontally), falls back to position directly below camera.
// Drawback: visible jump in map center when crossing the threshold (discontinuity at ~89° pitch).
//
// function getMapCameraTarget({
// 	cameraForward,
// 	cameraPosition,
// }: {
// 	cameraForward: THREE.Vector3;
// 	cameraPosition: THREE.Vector3;
// }) {
// 	const point = new THREE.Vector3();
//
// 	// Default: point directly below camera (fallback when ray is parallel to ground)
// 	let x = cameraPosition.x;
// 	let z = cameraPosition.z;
// 	let distance = cameraPosition.y;
//
// 	// Only compute ray intersection when camera has enough downward component.
// 	// Without this check: division by ~0 → Infinity → MapLibre crash.
// 	// With this check: smooth ray intersection for most angles, abrupt jump to below-camera at ~89° pitch.
// 	if (Math.abs(cameraForward.y) > 0.01) {
// 		const t = -cameraPosition.y / cameraForward.y;
// 		x = cameraPosition.x + cameraForward.x * t;
// 		z = cameraPosition.z + cameraForward.z * t;
// 		distance = Math.abs(t);
// 	}
//
// 	point.set(x, 0, z);
// 	return { point, distance };
// }

export { getMapCameraTarget };
